import { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { MessageCircle, X, Send } from "lucide-react";

export default function PrayerRequestModal({ onClose, onSaved, t }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isImportant, setIsImportant] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/prayer-requests`, {
        title,
        description,
        is_important: isImportant
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || (t('could_not_submit_prayer') || "Could not submit prayer request"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 rounded-xl p-2">
              <MessageCircle size={24} color="#9B5DE5" />
            </div>
            <h2 className="text-xl font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('prayer_request_title') || 'Prayer Request'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">{t('title_label') || 'Title'}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#9B5DE5] transition-colors"
              placeholder={t('prayer_title_placeholder') || "e.g., Healing for my grandmother"}
              required
              data-testid="prayer-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">{t('description_label') || 'Description'}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#9B5DE5] transition-colors resize-none"
              placeholder={t('prayer_desc_placeholder') || "Share more details about your prayer request..."}
              rows={4}
              required
              data-testid="prayer-description-input"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isImportant}
              onChange={(e) => setIsImportant(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-[#EF476F] focus:ring-[#EF476F]"
            />
            <span className="text-sm text-gray-600">
              {t('mark_as_urgent') || 'Mark as important/urgent'}
            </span>
          </label>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm font-medium p-3 rounded-xl">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold py-3 rounded-xl transition-all"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim() || !description.trim()}
              className="flex-1 bg-gradient-to-r from-[#9B5DE5] to-[#EF476F] hover:opacity-90 text-white font-black py-3 rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ fontFamily: "Fredoka, sans-serif" }}
              data-testid="submit-prayer-btn"
            >
              {loading ? (t('sending') || "Sending...") : <>
                <Send size={18} /> {t('submit') || 'Submit'}
              </>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
